import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Icon from "../../../Icon/Icon";
import "./EGFileUpload.scss";

/**
 * Component to upload and render files with MUI
 * @param {title} string Optional component title
 * @param {files} array Uploaded file data
 * @param {setFiles} function Setting the selected files
 * @param {customIcons} array Array of strings with the names of the icons to display
 * @returns
 */

const EGFileUpload = ({
  title = "",
  files = [],
  setFiles,
  formDataKey = "attachments",
  customIcons = [],
  disabled = false,
}) => {
  const [t] = useTranslation("components");
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const fileDivRef = useRef(null);
  const isDraggingOver = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const defaultIcons = [
    "gallery",
    "outline_document_PDF",
    "outline_document_text_2",
    "volume_high",
    "outline_video",
  ];

  const arrIcons = customIcons.length > 0 ? [...customIcons] : defaultIcons;

  useEffect(() => {
    // Every time the files change (they come from parent), clean input.
    // Selecting the same file triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [files]);

  // To generate formData
  const processFile = (file) => {
    const formData = new FormData();
    formData.append(formDataKey, file);
    return { file, formDataKey, formData };
  };

  // Input to load file
  const hdlFileInput = (e) => {
    if (disabled) return;
    const fileList = e.target.files;
    const newFileArray = Array.from(fileList).map(processFile);

    setFiles((prevFiles) => [
      ...prevFiles,
      ...newFileArray.filter(
        (newFile) =>
          !prevFiles.some(
            (prevFile) =>
              prevFile.file.name === newFile.file.name &&
              prevFile.file.size === newFile.file.size &&
              prevFile.file.lastModified === newFile.file.lastModified
          )
      ),
    ]);
    // Clean input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  // onClick event
  const hdlOnClick = () => {
    if (disabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Erase a file uploaded (Example)
  // const removeFile = (fileName) => {
  //   const updatedFiles = files.filter((file) => file.file.name !== fileName);
  //   setFiles(updatedFiles);
  // };

  // Sets the file when dropped
  const hdlFileDrop = (e) => {
    e.preventDefault();
    if (disabled) {
      fileDivRef.current?.classList.remove("drag-over");
      return;
    }
    const fileList = e.dataTransfer.files;
    const newFileArray = Array.from(fileList).map(processFile);

    setFiles((prevFiles) => [
      ...prevFiles,
      ...newFileArray.filter(
        (newFile) =>
          !prevFiles.some(
            (prevFile) =>
              prevFile.file.name === newFile.file.name &&
              prevFile.file.size === newFile.file.size &&
              prevFile.file.lastModified === newFile.file.lastModified
          )
      ),
    ]);

    fileDivRef.current.classList.remove("drag-over");
    // clean input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag files into input and add styles
  const hdlDragEnter = (e) => {
    e.preventDefault();
    if (disabled) return;
    isDraggingOver.current = true;
    fileDivRef.current.classList.add("drag-over");
    setIsDragging(true);
  };

  // Space calculation when the mouse leave input
  const hdlDragLeave = (e) => {
    e.preventDefault();
    const target = e.target;
    const clientX = e.clientX;
    const clientY = e.clientY;
    const elementBox = target.getBoundingClientRect();

    if (
      clientX < elementBox.left - 1 ||
      clientX > elementBox.right - 1 ||
      clientY < elementBox.top - 1 ||
      clientY > elementBox.bottom - 1
    ) {
      isDraggingOver.current = false;
      fileDivRef.current.classList.remove("drag-over");
      setIsDragging(false);
    }
  };

  // Remove styles drag and drop file
  const hdlMouseLeave = () => {
    if (isDraggingOver.current) {
      isDraggingOver.current = false;
      fileDivRef.current.classList.remove("drag-over");
      setIsDragging(false);
    }
  };
  // Drag file with mouse and prevent default browser behavior
  const preventDefaultBehavior = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box
      className="rounded"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      sx={{
        border: `${
          isDragging
            ? theme.palette.primary.main
            : theme.palette.blueGray200.main
        } 2px dashed`,
        position: "relative",
        px: 2,
        py: { xs: 5, sm: 2 },
        gap: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
      onClick={hdlOnClick}
      onDragOver={preventDefaultBehavior}
      onDrop={hdlFileDrop}
      onDragEnter={hdlDragEnter}
      onDragLeave={hdlDragLeave}
      onMouseLeave={hdlMouseLeave}
      ref={fileDivRef}
      aria-disabled={disabled || undefined}
    >
      {title && (
        <Typography
          sx={{
            position: "absolute",
            top: -13,
            left: 9,
            px: 0.75,
            background: theme.palette.background.paper,
          }}
          color={isDragging ? "primary" : "secondary"}
          variant="body2"
          fontWeight={600}
        >
          {title}
        </Typography>
      )}

      <Typography
        color={isDragging ? "deepIndigo" : "secondary"}
        variant="h6"
        textAlign="center"
      >
        {t("fileUpload.drag_drop_files")}
      </Typography>
      <Stack
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="row"
        flexWrap="wrap"
        spacing={1}
      >
        {arrIcons.map((icon, i) => (
          <Icon
            color={
              isDragging
                ? theme.palette.common.black
                : theme.palette.secondary.main
            }
            key={i}
            name={icon}
          />
        ))}
      </Stack>
      <Typography
        variant="body2"
        color={isDragging ? "success" : "secondary"}
        textAlign="center"
      >
        {t("fileUpload.or_click_here")}
      </Typography>
      <input
        type="file"
        accept="*"
        multiple
        ref={fileInputRef}
        onChange={hdlFileInput}
        hidden
        disabled={disabled}
        className="form-control w-100"
      />
    </Box>
  );
};

export default EGFileUpload;
